import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GraphQLFormattedError } from 'graphql';
import { Values } from '@lib/interfaces/form';
import Modal from '@lib/components/Modal/Modal';
import Form from '@lib/components/ReactHookForm/FormContainer';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import Trans from '@lib/components/Trans/Trans';
import { IconName } from '@lib/components/Modal/enums';
import { useCustomizeTabsFields } from './hooks/useCustomizeTabsFields';
import { CustomizeTabsActions } from './CustomizeTabsActions';
import hasDeletedTabs from './utils/hasDeletedTabs';

export interface CustomizeTabsModalFormProps {
  isOpen: boolean;
  isViewOnly: boolean;
  tabs: Array<{
    id: string;
    name: string;
    position: number;
    isDefault?: boolean;
  }>;
  onClose: () => void;
  onSubmit: (values: Values) => void;
  loading?: boolean;
  graphQLErrors?: readonly GraphQLFormattedError[];
}

export function CustomizeTabsModalForm({
  isOpen,
  isViewOnly,
  tabs,
  onClose,
  onSubmit,
  loading = false,
  graphQLErrors,
}: CustomizeTabsModalFormProps) {
  const { t } = useTranslation();
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
    useState(false);
  const [pendingValues, setPendingValues] = useState<Values | null>(null);

  const fields = useCustomizeTabsFields({ isViewOnly, t });

  const defaultValues = {
    tabs: tabs.map((tab) => ({
      id: tab.id,
      name: tab.name,
      position: tab.position,
      isDefault: tab.isDefault,
    })),
  };

  // Handle submit - show confirmation if tabs were deleted
  const handleSubmit = useCallback(
    (values: Values) => {
      if (hasDeletedTabs(values, tabs)) {
        // Store values and show confirmation modal
        setPendingValues(values);
        setIsDeleteConfirmModalOpen(true);
      } else {
        // No tabs deleted, submit directly
        onSubmit(values);
      }
    },
    [tabs, onSubmit],
  );

  // Handle confirmation of delete
  const handleConfirmDelete = useCallback(() => {
    setIsDeleteConfirmModalOpen(false);
    if (pendingValues) {
      onSubmit(pendingValues);
      setPendingValues(null);
    }
  }, [onSubmit, pendingValues]);

  // Handle cancel of delete confirmation
  const handleCancelDelete = useCallback(() => {
    setIsDeleteConfirmModalOpen(false);
    setPendingValues(null);
  }, []);

  const actions = () => (
    <CustomizeTabsActions onCancel={onClose} loading={loading} />
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} caption={t('customize-tabs')}>
        <Form
          formId="customize-tabs-form"
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          fields={fields}
          actions={actions}
          disableAwayConfirmation
          loading={loading}
          graphQLErrors={graphQLErrors}
          graphQLErrorsReplacePathMap={{
            homePages: 'tabs', // TODO: graphQLErrorsReplacePathMap prop does not work for nested fields yet
          }}
        />
      </Modal>
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="confirm-delete-tab-text" />}
        icon={IconName.Delete}
        isOpen={isDeleteConfirmModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('are-you-sure')}
      />
    </>
  );
}
